// Standalone "Buy Add-ons" page at /shop/addons.
//
// Used by paid customers (active subscription) who want to top up extra
// clients or AI responses without changing their plan. Clients and
// responses are a single org-wide pool shared across all portals (quotas
// were unified on 1 Jul 2026), so this page buys ONE clients total and ONE
// responses total, not per-portal amounts. Reuses the same Razorpay popup →
// /verify flow as the main /shop catalogue but omits plan_code from the
// request so the BE applies the addons against the org's existing
// subscription.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Divider,
  InputNumber,
  Skeleton,
  Space,
  Statistic,
  Typography,
  message as antMessage,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Crown } from "lucide-react";

import { fetchData, postData } from "../../api/apiService";
import { PAYMENTS, SUBSCRIPTIONS } from "../../api/endpointMap";
import { useAuth } from "../../context/AuthContext";
import { useSubscriptionStatus } from "../../hooks/useSubscriptionStatus";
import {
  resetSubscriptionStatusCache,
} from "../../hooks/useSubscriptionStatus";
import { useRazorpayCheckout } from "../../components/payments/useRazorpayCheckout";
import TaxInvoiceModal from "../../components/TaxInvoiceModal";

const { Title, Paragraph, Text } = Typography;

const PORTAL_DEFS = [
  { key: "income_tax", label: "Income Tax", wire: "Income Tax" },
  { key: "gst", label: "GST", wire: "GST" },
  { key: "tds", label: "TDS", wire: "TDS" },
];

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);
}

export default function AddonsPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { status, isLoading: statusLoading } = useSubscriptionStatus();
  const { openCheckout } = useRazorpayCheckout();

  // Cross-module addon quantities. Clients and responses are a single
  // org-wide pool now (quotas were unified across Income Tax / GST / TDS
  // on 1 Jul 2026), so the page buys ONE "extra clients" and ONE "extra
  // responses" total rather than three per-portal amounts. See addonsBody
  // below for how the single pool maps onto the still-per-portal BE
  // request shape.
  const [addonClients, setAddonClients] = useState(0);
  const [addonResponses, setAddonResponses] = useState(0);

  // Pricing payload returned by /subscriptions/shop-pricing.
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [isPricing, setIsPricing] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  // Tax-invoice gate — first-time buyers must fill billing details
  // before Razorpay opens. After save, re-runs handleBuy automatically.
  const [taxInvoiceOpen, setTaxInvoiceOpen] = useState(false);
  const [pendingBuy, setPendingBuy] = useState(false);
  // One-shot bypass for the tax-invoice gate — see Shop.jsx ConfigureDrawer
  // for the full rationale. resetSubscriptionStatusCache only nulls the
  // module cache; the hook's React state stays stale on the immediate
  // setTimeout(handleBuy, 250) retry. BE re-validates server-side.
  const justSavedTaxInvoiceRef = useRef(false);

  // Per-unit add-on rates sourced from the customer's current plan row
  // (addon_client_price_per_unit / addon_response_price_per_unit on
  // subscription_plans). Loaded once via /subscriptions/plans so each
  // input can show "₹50/client" / "₹200/response" hint inline, even
  // before the user enters any quantity (no /shop-pricing round-trip
  // needed for the hint).
  const [planRates, setPlanRates] = useState({
    client: null,
    response: null,
  });

  // Active subscription gate. The standalone purchase only makes
  // sense for paid customers with an active plan. Trial users and
  // expired users get bounced to /shop to pick a plan first.
  const activePlan = status?.subscription_plan;
  const isTrial = !!status?.is_trial;
  const isExpired = !!(status?.is_trial && status?.trial_expired_at);
  const canBuyAddons = !!activePlan && !isTrial && !isExpired;

  // Which portals does the customer's plan currently enable?
  const enabledPortals = useMemo(() => {
    const e = {};
    (status?.portals || []).forEach((p) => {
      const k = p.portal === "Income Tax" ? "income_tax" : p.portal.toLowerCase();
      e[k] = !!p.enabled;
    });
    return e;
  }, [status]);

  // The single cross-module pool is bought against ONE enabled portal in
  // the BE request. This is deliberate: the enforced quota is the org-wide
  // pair (organizations.max_client_allowed / max_response_generation_allowed),
  // which apply_addon_purchase increments by the SUM of the per-portal
  // amounts, and the per-portal cap columns are retained-but-unread. So
  // routing the whole pool through the first enabled portal charges and
  // applies identically to spreading it, while keeping the exact request
  // shape the /verify apply path reads (it reads the six per-portal keys,
  // NOT the legacy flat pair, which is written to order notes but never
  // read back, so it must not be used here).
  const firstEnabledPortal = useMemo(
    () => PORTAL_DEFS.find((p) => enabledPortals[p.key])?.key || "income_tax",
    [enabledPortals],
  );

  // Body shape consumed by both /shop-pricing (preview) and
  // /payments/razorpay/create-order (purchase). The single pool is placed
  // on `firstEnabledPortal`; every other portal stays zero.
  const addonsBody = useMemo(() => {
    const clients = Number(addonClients) || 0;
    const responses = Number(addonResponses) || 0;
    const zero = {
      additional_income_tax_clients: 0,
      additional_income_tax_responses: 0,
      additional_gst_clients: 0,
      additional_gst_responses: 0,
      additional_tds_clients: 0,
      additional_tds_responses: 0,
    };
    return {
      ...zero,
      [`additional_${firstEnabledPortal}_clients`]: clients,
      [`additional_${firstEnabledPortal}_responses`]: responses,
    };
  }, [addonClients, addonResponses, firstEnabledPortal]);

  const totalUnits = useMemo(
    () => (Number(addonClients) || 0) + (Number(addonResponses) || 0),
    [addonClients, addonResponses],
  );

  // Does the customer's CURRENT plan include AI responses? Essential
  // plans have max_responses=0 on every portal; Advanced plans have
  // positive values. Mirrors the Shop.jsx planHasAI check so the same
  // "hide Extra responses + full-width Extra clients" UX applies here
  // for Essential-plan customers buying add-ons.
  const planHasAI = useMemo(
    () => (status?.portals || []).some((p) => (p.max_responses ?? 0) > 0),
    [status],
  );

  const recompute = useCallback(async () => {
    if (!canBuyAddons || !activePlan) return;
    if (totalUnits === 0) {
      setPricing(null);
      setPricingError(null);
      return;
    }
    setIsPricing(true);
    setPricingError(null);
    try {
      // Use the customer's current plan as the rate source so per-tier
      // addon prices on that plan apply.
      const portalsList = PORTAL_DEFS
        .filter((p) => enabledPortals[p.key])
        .map((p) => p.wire);
      const raw = await postData(SUBSCRIPTIONS.SHOP_PRICING, {
        plan_code: activePlan,
        selected_portals: portalsList,
        cadence: "Yearly",
        ...addonsBody,
      });
      const payload = raw?.data?.result || raw?.result || raw?.data || raw || {};
      setPricing(payload);
    } catch (e) {
      setPricingError(
        e?.detail || e?.message || "Could not compute pricing. Please retry.",
      );
      setPricing(null);
    } finally {
      setIsPricing(false);
    }
  }, [canBuyAddons, activePlan, totalUnits, addonsBody, enabledPortals]);

  useEffect(() => {
    const t = setTimeout(() => recompute(), 250);
    return () => clearTimeout(t);
  }, [recompute]);

  // Hydrate per-unit rates from the active plan row. Mirrors what
  // Shop.jsx does (also reads the same /plans endpoint). Bails out if
  // there's no active plan; canBuyAddons gate already covers that case
  // in the render.
  useEffect(() => {
    if (!activePlan) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchData(SUBSCRIPTIONS.PLANS);
        const payload = raw?.data?.result || raw?.result || raw?.data || raw || {};
        const all = [];
        for (const arr of Object.values(payload)) {
          if (Array.isArray(arr)) all.push(...arr);
        }
        const match = all.find((p) => p?.plan_code === activePlan);
        if (!cancelled && match) {
          setPlanRates({
            client: Number(match.addon_client_price_per_unit) || null,
            response: Number(match.addon_response_price_per_unit) || null,
          });
        }
      } catch (_e) {
        // Hint is non-critical — if /plans fails, the input still works
        // and the order summary shows the per-unit cost after pricing.
      }
    })();
    return () => { cancelled = true; };
  }, [activePlan]);

  // Compute the addon-only cost from the breakdown (the BE response
  // includes both the subscription base AND the addons; the standalone
  // purchase will be charged only the addons + GST on that amount, so
  // mirror that math here for the order summary).
  const addonsPreview = useMemo(() => {
    if (!pricing) return null;
    const addons = pricing.breakdown?.addons || {};
    const gstBlock = pricing.breakdown?.gst || {};
    const subtotal = Number(addons.addons_total || 0);
    const gstPct = Number(gstBlock.percent || 0);
    const gst = gstBlock.enabled ? Math.round(subtotal * gstPct) / 100 : 0;
    return {
      subtotal,
      gst,
      total: Math.round((subtotal + gst) * 100) / 100,
      addons,
    };
  }, [pricing]);

  const handleBuy = async () => {
    if (!canBuyAddons) {
      antMessage.error(
        "Add-ons are available only with an active paid subscription.",
      );
      return;
    }
    if (totalUnits === 0) {
      antMessage.error("Add at least one extra client or response before paying.");
      return;
    }
    if (!addonsPreview || !(addonsPreview.total > 0)) {
      antMessage.error("Order amount is not ready. Please retry.");
      return;
    }

    // Tax-invoice gate — first-time buyers fill billing details
    // before the Razorpay popup. BE also enforces this (defense in
    // depth) with structured 400 `code: tax_invoice_required`.
    if (justSavedTaxInvoiceRef.current) {
      justSavedTaxInvoiceRef.current = false;
    } else if (status && !status.is_tax_invoice_submitted) {
      setPendingBuy(true);
      setTaxInvoiceOpen(true);
      return;
    }

    setIsPurchasing(true);
    try {
      // No plan_code → BE treats this as addon-only against the active
      // subscription, recomputes the amount from the org's current plan
      // rates, and ignores any FE-supplied total.
      const orderRaw = await postData(PAYMENTS.RAZORPAY_CREATE_ORDER, {
        plan_code: null,
        ...addonsBody,
      });
      const orderResp =
        orderRaw?.data?.result || orderRaw?.result || orderRaw?.data || orderRaw || {};
      if (!orderResp.razorpay_order_id || !orderResp.key_id) {
        throw new Error("Order creation returned an unexpected response.");
      }

      const result = await openCheckout({
        orderResp,
        planName: "Add-ons top-up",
        description: "Extra clients / responses",
        prefill: {
          name: user?.full_name || "",
          email: user?.email || "",
        },
      });

      if (result.kind === "dismissed") {
        try {
          await postData(PAYMENTS.RAZORPAY_CANCEL_ORDER, {
            order_id: orderResp.order_id,
            reason: "user_dismissed_popup",
          });
        } catch (_e) {
          // ignore — cancel is best-effort.
        }
        antMessage.info("Payment cancelled. You can retry anytime.");
        return;
      }
      if (result.kind === "failed") {
        try {
          await postData(PAYMENTS.RAZORPAY_CANCEL_ORDER, {
            order_id: orderResp.order_id,
            reason: `gateway_failed: ${result.code || "unknown"}`,
          });
        } catch (_e) {
          // ignore
        }
        antMessage.error(
          result.description || "Payment failed. Please try again.",
        );
        return;
      }

      // Success: verify with the BE to apply the addons.
      await postData(PAYMENTS.RAZORPAY_VERIFY, {
        order_id: orderResp.order_id,
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });

      antMessage.success("Add-ons applied to your subscription!");
      // Reset the subscription cache so the Usage page meters reflect
      // the new caps on the next render.
      resetSubscriptionStatusCache();
      navigate("/usage");
    } catch (e) {
      antMessage.error(
        e?.detail || e?.message || "Payment could not be completed. Please retry.",
      );
    } finally {
      setIsPurchasing(false);
    }
  };

  // ------------------------------ render ------------------------------

  if (statusLoading) {
    return (
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (!canBuyAddons) {
    return (
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <div className="flex items-center justify-between mb-4">
          <Title level={2} style={{ marginBottom: 0, color: "#0a1e54" }}>
            Buy Add-ons
          </Title>
          <Link to="/shop">
            <Button icon={<ArrowLeftOutlined />}>Back to plans</Button>
          </Link>
        </div>
        <Alert
          type="info"
          showIcon
          message="Add-ons are available with an active paid subscription"
          description={
            isTrial
              ? "You're on a free trial. Pick a paid plan first; you can then buy extras anytime."
              : "Your subscription isn't currently active. Pick a plan to continue."
          }
          action={
            <Link to="/shop">
              <Button type="primary" icon={<Crown size={14} strokeWidth={2.25} />}>
                See plans
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <Title level={2} style={{ marginBottom: 4, color: "#0a1e54" }}>
            Buy Add-ons
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Top up extra clients or AI responses on your{" "}
            <strong>{activePlan}</strong> plan. Extras are shared across all
            your portals and charged per unit at your plan's add-on rates.
          </Paragraph>
        </div>
        <Link to="/shop">
          <Button icon={<ArrowLeftOutlined />}>Back to plans</Button>
        </Link>
      </div>

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {/* Single cross-module purchase. Clients and responses are one
            org-wide pool that applies across Income Tax, GST and TDS, so
            there is one input each rather than a card per portal. */}
        <Card
          size="small"
          styles={{ body: { padding: 16 } }}
          style={{ borderRadius: 10 }}
        >
          <div
            className={`grid ${
              planHasAI ? "grid-cols-2" : "grid-cols-1"
            } gap-3`}
          >
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Extra clients
              </Text>
              <InputNumber
                min={0}
                step={1}
                value={addonClients}
                onChange={(v) => setAddonClients(Math.max(0, Number(v) || 0))}
                parser={(v) => (v || "").replace(/\D/g, "")}
                style={{ width: "100%" }}
              />
              {planRates.client !== null && (
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: "block", marginTop: 4 }}
                >
                  {formatINR(planRates.client)} / client, used across all portals
                </Text>
              )}
            </div>
            {/* Extra responses, only for plans with AI quota (Pro / Prime).
                Track-tier customers have no response pool and see Extra
                clients full-width. */}
            {planHasAI && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Extra responses
                </Text>
                <InputNumber
                  min={0}
                  step={1}
                  value={addonResponses}
                  onChange={(v) => setAddonResponses(Math.max(0, Number(v) || 0))}
                  parser={(v) => (v || "").replace(/\D/g, "")}
                  style={{ width: "100%" }}
                />
                {planRates.response !== null && (
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block", marginTop: 4 }}
                  >
                    {formatINR(planRates.response)} / response, used across all portals
                  </Text>
                )}
              </div>
            )}
          </div>
        </Card>

        <Card style={{ borderRadius: 10 }}>
          <Title level={5} style={{ marginTop: 0 }}>
            Order summary
          </Title>
          {pricingError && (
            <Alert
              type="warning"
              showIcon
              message={pricingError}
              className="mb-3"
            />
          )}
          {isPricing && <Skeleton active paragraph={{ rows: 3 }} />}
          {!isPricing && totalUnits === 0 && (
            <Text type="secondary">
              Add at least one extra unit above to see the price.
            </Text>
          )}
          {!isPricing && addonsPreview && totalUnits > 0 && (
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              {/* Two flat lines, because clients and responses are a single
                  pool and the summary is not itemised per portal. Line
                  amounts are quantity times the plan's uniform per-unit rate;
                  a line is shown only when its rate is known, and the
                  authoritative subtotal, GST and total below always come from
                  the BE pricing response. */}
              {(Number(addonClients) || 0) > 0 && planRates.client !== null && (
                <Fragment key="line-c">
                  <Text>Extra clients ({Number(addonClients) || 0})</Text>
                  <Text style={{ textAlign: "right" }}>
                    {formatINR((Number(addonClients) || 0) * planRates.client)}
                  </Text>
                </Fragment>
              )}
              {(Number(addonResponses) || 0) > 0 && planRates.response !== null && (
                <Fragment key="line-r">
                  <Text>Extra responses ({Number(addonResponses) || 0})</Text>
                  <Text style={{ textAlign: "right" }}>
                    {formatINR((Number(addonResponses) || 0) * planRates.response)}
                  </Text>
                </Fragment>
              )}

              <Divider style={{ gridColumn: "1 / -1", margin: "4px 0" }} />

              <Text strong>Add-ons subtotal</Text>
              <Text strong style={{ textAlign: "right" }}>
                {formatINR(addonsPreview.subtotal)}
              </Text>

              {addonsPreview.gst > 0 && (
                <>
                  <Text type="secondary">GST (18%)</Text>
                  <Text type="secondary" style={{ textAlign: "right" }}>
                    {formatINR(addonsPreview.gst)}
                  </Text>
                </>
              )}

              <Divider style={{ gridColumn: "1 / -1", margin: "8px 0" }} />

              <Text strong>Total payable</Text>
              <Statistic
                value={addonsPreview.total}
                precision={0}
                valueStyle={{
                  textAlign: "right",
                  color: "#0a1e54",
                  fontWeight: 700,
                  fontSize: 24,
                }}
                prefix="₹"
              />
            </div>
          )}
        </Card>

        <Button
          type="primary"
          size="large"
          block
          loading={isPurchasing}
          disabled={
            isPurchasing
            || isPricing
            || totalUnits === 0
            || !addonsPreview
            || !(addonsPreview.total > 0)
          }
          onClick={handleBuy}
          style={{ height: 48, fontWeight: 600 }}
        >
          {totalUnits === 0
            ? "Add extras to enable purchase"
            : `Pay ${formatINR(addonsPreview?.total)} & top up`}
        </Button>
      </Space>

      {/* Tax-invoice first-time capture (same modal as /shop). After
          save, refresh status cache and re-run handleBuy so the
          Razorpay popup fires without a second click. */}
      <TaxInvoiceModal
        open={taxInvoiceOpen}
        companyName={status?.company_name}
        // Pre-fill from BE-saved values so admin-unsubmit flows (R13)
        // show the previous data instead of a blank form. First-time
        // submitters get all-null → modal renders empty as before.
        initialValue={{
          is_gst_registered: status?.is_gst_registered,
          tax_invoice_gst_number: status?.tax_invoice_gst_number,
          tax_invoice_address: status?.tax_invoice_address,
          tax_invoice_city: status?.tax_invoice_city,
          tax_invoice_state: status?.tax_invoice_state,
          tax_invoice_pincode: status?.tax_invoice_pincode,
        }}
        onCancel={() => {
          setTaxInvoiceOpen(false);
          setPendingBuy(false);
        }}
        onSaved={() => {
          setTaxInvoiceOpen(false);
          resetSubscriptionStatusCache();
          if (pendingBuy) {
            setPendingBuy(false);
            // One-shot bypass — see Shop.jsx for rationale.
            justSavedTaxInvoiceRef.current = true;
            setTimeout(() => handleBuy(), 250);
          }
        }}
      />
    </div>
  );
}
